import React, { createContext, useContext } from "react"

import classNames from "classnames"

import { connect } from "scrivito"
import { BuildingReferenceObj } from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import { ScrivitoImage } from "scrivito/scrivito-image/ScrivitoImage"
import { ScrivitoLink } from "scrivito/scrivito-link/ScrivitoLink"
import { ensureString } from "shared/lib/ensureString"
import { WithChildren } from "shared/types/WithChildren"
import { WithClassName } from "shared/types/WithClassName"

import { useBuildingReferenceInfoText } from "entities/building-references/lib/useBuildingReferenceInfoText"
import { BuildingReferenceCard } from "entities/building-references/ui/building-reference-card/BuildingReferenceCard"

const ScrivitoBuildingReferenceCardContext =
  createContext<BuildingReferenceObj | null>(null)

function useScrivitoBuildingReferenceCardContext() {
  const ctx = useContext(ScrivitoBuildingReferenceCardContext)
  if (!ctx)
    throw new Error("Must be used inside ScrivitoBuildingReferenceCard.Root")
  return ctx
}

interface ScrivitoBuildingReferenceCardRootProps
  extends WithChildren,
    WithClassName {
  buildingReferenceObj: BuildingReferenceObj
  linkClassName?: string
}

function ScrivitoBuildingReferenceCardRootComponent({
  buildingReferenceObj,
  className,
  linkClassName,
  children
}: ScrivitoBuildingReferenceCardRootProps) {
  return (
    <ScrivitoBuildingReferenceCardContext.Provider value={buildingReferenceObj}>
      <ScrivitoLink
        to={buildingReferenceObj}
        className={classNames("anchor", linkClassName)}
      >
        <BuildingReferenceCard.Root className={className}>
          {children}
        </BuildingReferenceCard.Root>
      </ScrivitoLink>
    </ScrivitoBuildingReferenceCardContext.Provider>
  )
}

export const ScrivitoBuildingReferenceCardRoot = React.memo(
  connect(ScrivitoBuildingReferenceCardRootComponent)
) as typeof ScrivitoBuildingReferenceCardRootComponent

function ScrivitoBuildingReferenceCardTitleComponent({
  className
}: WithClassName) {
  const obj = useScrivitoBuildingReferenceCardContext()
  return (
    <BuildingReferenceCard.Title className={className}>
      {obj.get("title")}
    </BuildingReferenceCard.Title>
  )
}

const ScrivitoBuildingReferenceCardTitle = React.memo(
  connect(ScrivitoBuildingReferenceCardTitleComponent)
) as typeof ScrivitoBuildingReferenceCardTitleComponent

function ScrivitoBuildingReferenceCardImageComponent({
  className
}: WithClassName) {
  const obj = useScrivitoBuildingReferenceCardContext()
  const image = obj.get("imageWidgets")?.[0]
  return (
    <BuildingReferenceCard.Image className={className}>
      <ScrivitoImage
        content={image || null}
        attribute="image"
        alt={ensureString(image?.get("alternativeText"))}
      />
    </BuildingReferenceCard.Image>
  )
}

const ScrivitoBuildingReferenceCardImage = React.memo(
  connect(ScrivitoBuildingReferenceCardImageComponent)
) as typeof ScrivitoBuildingReferenceCardImageComponent

function ScrivitoBuildingReferenceCardInfoTextComponent({
  className
}: WithClassName) {
  const obj = useScrivitoBuildingReferenceCardContext()
  const infoText = useBuildingReferenceInfoText(obj)

  return (
    <BuildingReferenceCard.InfoText className={className}>
      {infoText}
    </BuildingReferenceCard.InfoText>
  )
}

const ScrivitoBuildingReferenceCardInfoText = React.memo(
  connect(ScrivitoBuildingReferenceCardInfoTextComponent)
) as typeof ScrivitoBuildingReferenceCardInfoTextComponent

export const ScrivitoBuildingReferenceCard = {
  Root: ScrivitoBuildingReferenceCardRoot,
  Title: ScrivitoBuildingReferenceCardTitle,
  Image: ScrivitoBuildingReferenceCardImage,
  InfoText: ScrivitoBuildingReferenceCardInfoText
}
